<template>
    <div>
        <q-dialog :model-value="show" @hide="cancel">
            <q-card class="q-pa-md split-dialog">
                <q-card-section class="q-pt-none">
                    <q-form ref="form" @submit="confirm">
                        <div class="text-h6 q-mb-md">
                            {{ $t('Split phase') }}
                        </div>
                        <div>
                            <div class="q-my-md">
                                <span>
                                    <b class="q-mr-xs">{{ $t('Status') }}: </b>
                                    <q-icon
                                        name="fas fa-circle"
                                        :color="
                                            ASSET_STATUS_TO_STYLE[status][
                                                'color'
                                            ]
                                        "
                                    />
                                    {{
                                        $t(
                                            ASSET_STATUS_TO_STYLE[status][
                                                'label'
                                            ]
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="q-my-md">
                                <b class="q-mr-xs">{{ $t('Start') }}: </b>
                                {{ dateFormat(start, userLocale) }}
                            </div>
                        </div>
                        <date-time-picker
                            id="split-time-input"
                            v-model="form.datetime"
                            :label="$t('Split the phase at the time')"
                            :rules="formRules.datetime"
                            :dateRange="{ start, end }"
                            :disable="isLoading"
                        />
                        <div
                            class="form__actions"
                            :class="{ 'q-mt-lg': $q.screen.lt.md }"
                        >
                            <q-btn
                                id="confirm-split-button"
                                @click="confirm"
                                :loading="isLoading"
                                :label="$t('Confirm')"
                                class="float-right"
                                color="primary"
                            />
                            <q-btn
                                @click="cancel"
                                :disable="isLoading"
                                :label="$t('Cancel')"
                                class="float-right q-mr-sm"
                                text-color="primary"
                                color="white"
                                outline
                                v-close-popup
                            />
                        </div>
                    </q-form>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import {
    LOCALE_DATETIME_FORMAT,
    getLocaleFormat
} from '@models/timeformats.js';
import { date } from 'quasar';
import { mapGetters } from 'vuex';
import AsyncManager from '@utils/asyncManager.js';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'SplitPhaseDialog',
    components: { DateTimePicker },
    emits: ['split', 'close'],
    props: {
        id: {
            type: Number,
            required: true
        },
        start: {
            type: String,
            required: true
        },
        end: {
            type: String
        },
        status: {
            type: String,
            required: true
        },
        show: {
            type: Boolean,
            required: true
        }
    },
    created() {
        // initialize async manager instance
        this.asyncManager = new AsyncManager({});
        // ensure that the initial date is within the valid date range
        const startDate = new Date(this.start);
        this.form.datetime = date
            .addToDate(startDate, {
                seconds: 1,
                milliseconds: new Date(this.start).getMilliseconds()
            })
            .toISOString();
    },
    beforeUnmount() {
        // let the celery task continue executing but stop periodic status check
        try {
            this.asyncManager.stopPeriodicStatusCheck();
        } catch (error) {
            console.error(error.message);
        }
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            LOCALE_DATETIME_FORMAT,

            form: {
                datetime: null
            },
            formRules: {
                datetime: [
                    val =>
                        this.validateSplitTime(val) ||
                        this.$t(
                            'Select a datetime within the start and end of this phase.'
                        )
                ]
            },
            isLoading: false
        };
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Payload in server format.
         */
        payload() {
            return { datetime: this.form.datetime };
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        dateFormat(val, locale) {
            return getLocaleFormat(val, locale);
        },
        /**
         * Send request to server.
         */
        async confirm() {
            this.isLoading = true;

            // validate the split time on click of confirm button
            const splitDatetime = getLocaleFormat(
                this.form.datetime,
                this.userLocale
            );
            if (!this.validateSplitTime(splitDatetime)) {
                this.$notifyError(
                    this.$t('Cannot split the phase at a future date/time.')
                );
                this.isLoading = false;
                return;
            }

            const url = `assetphases/${this.id}/split/`;
            //  disable the confirm button
            const payload = {
                date: this.form.datetime,
                asynchronous: true
            };
            try {
                const result = await this.asyncManager.asyncLoad(url, payload);
                if ('error' in result[0]) {
                    // error during split execution
                    const error = result[0]['error'];
                    if (error === 'unprocessed_phases') {
                        this.$notifyError(
                            this.$t(
                                'Data for this phase is still processing. Please try again later.'
                            )
                        );
                    } else if (error === 'latest_phase') {
                        this.$notifyError(
                            this.$t(
                                'Failed to split phase. The selected phase is no longer the latest phase.'
                            )
                        );
                    } else if (error === 'split_date') {
                        this.$notifyError(this.$t('Split date is invalid.'));
                    } else if (error == 'uneditable_phase') {
                        this.$notifyError(
                            this.$t(
                                "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                {
                                    editablePeriod: parseInt(
                                        process.env
                                            .EDITABLE_PHASE_DURATION_DAYS || 5 // 5 days
                                    )
                                }
                            )
                        );
                    } else {
                        // unknown error
                        this.$notifyError(
                            this.$t(
                                'Failed to split phase. Please try again later.'
                            ),
                            error
                        );
                    }
                } else {
                    //split was successful
                    // notify user
                    this.$notifySuccess(this.$t('Phase has been split.'));
                    // success
                    this.$emit('split');
                }
            } catch (error) {
                if (
                    error &&
                    typeof error == 'object' &&
                    'uneditable_phase' in error
                ) {
                    this.$notifyError(
                        this.$t(
                            "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                            {
                                editablePeriod: parseInt(
                                    process.env.EDITABLE_PHASE_DURATION_DAYS ||
                                        5 // 5 days
                                )
                            }
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to split phase. Please try again later.'
                        ),
                        error
                    );
                }
            }

            this.isLoading = false;
        },
        /**
         * Close the dialog.
         */
        cancel() {
            this.$emit('close');
        },
        /**
         * Check that the time to split the phase is within the phase start and end.
         * @param {Date} datetime
         * @return {Boolean}
         */
        validateSplitTime(datetime) {
            // use cross browser datetime format
            let end_date = this.end ? new Date(this.end) : new Date();
            if (datetime) {
                let formattedDatetime = datetime.replace(/-/g, '/');
                return (
                    new Date(this.start) < new Date(formattedDatetime) &&
                    new Date(formattedDatetime) < end_date
                );
            }
            return false;
        }
    }
};
</script>

<style lang="scss" scoped>
.split-dialog {
    width: 90%;
}
</style>
